import { Sparkle, ShieldWarning, X, ArrowRight } from "@phosphor-icons/react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import type { DashboardAiInsight } from "./types";

export interface DashboardAiInsightCardProps {
  insight: DashboardAiInsight;
  dismissLabel: string;
  onCta: (insight: DashboardAiInsight) => void;
  onDismiss: (insight: DashboardAiInsight) => void;
  index?: number;
}

const SEVERITY_STYLES = {
  info: {
    container: "border-biz-info-border bg-biz-info-subtle/60",
    iconWrap: "bg-biz-info text-biz-info-foreground",
    title: "text-biz-foreground",
    badge: "bg-biz-info/10 text-biz-info",
  },
  warning: {
    container: "border-biz-warning-border bg-biz-warning-subtle/60",
    iconWrap: "bg-biz-warning text-biz-warning-foreground",
    title: "text-biz-foreground",
    badge: "bg-biz-warning/15 text-biz-warning-foreground",
  },
  critical: {
    container: "border-biz-danger-border bg-biz-danger-subtle/60",
    iconWrap: "bg-biz-danger text-biz-danger-foreground",
    title: "text-biz-foreground",
    badge: "bg-biz-danger/15 text-biz-danger",
  },
} as const;

export function DashboardAiInsightCard({
  insight,
  dismissLabel,
  onCta,
  onDismiss,
  index = 0,
}: DashboardAiInsightCardProps) {
  const styles = SEVERITY_STYLES[insight.severity];
  const Icon = insight.severity === "info" ? Sparkle : ShieldWarning;
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      layout
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scaleY: 0.6 }}
      transition={{ duration: 0.18, delay: reduceMotion ? 0 : index * 0.06, ease: [0.4, 0, 0.2, 1] }}
      className={cn(
        "relative flex min-h-[140px] flex-col gap-2 rounded-biz-card border p-4",
        styles.container,
        "biz-motion-safe",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={cn("flex h-8 w-8 items-center justify-center rounded-biz-btn", styles.iconWrap)}>
          <Icon className="h-4 w-4" weight={insight.severity === "critical" ? "fill" : "regular"} aria-hidden />
        </div>
        <button
          type="button"
          onClick={() => onDismiss(insight)}
          aria-label={dismissLabel}
          className="-mr-1 -mt-1 flex h-7 w-7 items-center justify-center rounded-biz-btn text-biz-foreground-muted transition-colors hover:bg-biz-muted hover:text-biz-foreground biz-focus-ring"
        >
          <X className="h-3.5 w-3.5" weight="bold" aria-hidden />
        </button>
      </div>
      <h4 className={cn("text-sm font-semibold leading-snug", styles.title)}>{insight.title}</h4>
      <p className="text-xs leading-relaxed text-biz-foreground-muted">{insight.description}</p>
      <div className="mt-auto pt-2">
        <button
          type="button"
          onClick={() => onCta(insight)}
          className="inline-flex h-9 items-center gap-1.5 rounded-biz-btn bg-biz-foreground px-3 text-xs font-semibold text-biz-bg transition-colors hover:bg-biz-foreground/85 biz-focus-ring"
        >
          {insight.ctaLabel}
          <ArrowRight className="h-3.5 w-3.5" weight="bold" aria-hidden />
        </button>
      </div>
    </motion.div>
  );
}

export interface DashboardAiInsightStripProps {
  insights: DashboardAiInsight[];
  sectionTitle: string;
  sectionSubtitle: string;
  emptyLabel: string;
  dismissLabel: string;
  onCta: (insight: DashboardAiInsight) => void;
  onDismiss: (insight: DashboardAiInsight) => void;
  className?: string;
}

export function DashboardAiInsightStrip({
  insights,
  sectionTitle,
  sectionSubtitle,
  emptyLabel,
  dismissLabel,
  onCta,
  onDismiss,
  className,
}: DashboardAiInsightStripProps) {
  return (
    <section className={cn("flex flex-col gap-3", className)} aria-labelledby="dash-ai-title">
      <header className="flex items-baseline justify-between gap-3">
        <div>
          <h3 id="dash-ai-title" className="text-base font-semibold text-biz-foreground">
            {sectionTitle}
          </h3>
          <p className="text-xs text-biz-foreground-muted">{sectionSubtitle}</p>
        </div>
      </header>
      {insights.length === 0 ? (
        <div className="rounded-biz-card border border-dashed border-biz-border bg-biz-surface px-4 py-6 text-center text-sm text-biz-foreground-muted">
          {emptyLabel}
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <AnimatePresence initial={false}>
            {insights.map((insight, i) => (
              <DashboardAiInsightCard
                key={insight.id}
                insight={insight}
                dismissLabel={dismissLabel}
                onCta={onCta}
                onDismiss={onDismiss}
                index={i}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </section>
  );
}
